import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Briefcase, Clock, CheckCircle2, DollarSign, AlertCircle, RotateCcw } from 'lucide-react';
import { fmtDate, payrollTypeLabel } from '@/components/payroll/payrollFmt';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const STATUS_META = {
  eligible: { t: 'Eligible for Payroll Run', c: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  held_awaiting_payment: { t: 'Held — Awaiting Customer Payment', c: 'bg-amber-100 text-amber-700', icon: Clock },
  recorded_no_pay: { t: 'Recorded — In Regular Compensation', c: 'bg-slate-100 text-slate-600', icon: Briefcase },
  added_to_run: { t: 'Added to Payroll Run', c: 'bg-blue-100 text-blue-700', icon: DollarSign },
  cancelled: { t: 'Cancelled', c: 'bg-slate-100 text-slate-500', icon: AlertCircle },
};
const ORDER = ['eligible', 'held_awaiting_payment', 'added_to_run', 'recorded_no_pay'];

const PayrollWorkOrderQueue = ({ meta }) => {
  const { token } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [runs, setRuns] = useState([]);
  const [sel, setSel] = useState({});          // itemId -> run_id
  const [pay, setPay] = useState({});           // itemId -> {payment_date, reference}
  const [loading, setLoading] = useState(true);
  const canPay = !!(meta?.can_approve || meta?.is_global_admin);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/payroll/wo-items`, auth(token)),
        axios.get(`${API}/payroll/runs`, auth(token)),
      ]);
      setItems(a.data.items || []); setCounts(a.data.counts || {});
      setRuns((b.data || []).filter((r) => r.status === 'draft'));
    } catch (e) { toast.error('Unable to load the work-order pay queue'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const runLabel = (r) => `${payrollTypeLabel(r.group)} · ${String(r.month).padStart(2, '0')}/${String(r.year).slice(2)}`;
  const eligibleRuns = (item) => {
    if (item.payment_timing === 'specified_future') {
      return runs.filter((r) => r.group === item.specified_group && r.year === item.specified_year && r.month === item.specified_month);
    }
    return runs;
  };

  const addToRun = async (item) => {
    const run_id = sel[item.id];
    if (!run_id) { toast.error('Select a draft run'); return; }
    try {
      await axios.post(`${API}/payroll/wo-items/${item.id}/add-to-run`, { run_id }, auth(token));
      toast.success('Added to payroll run'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add to run'); }
  };
  const removeFromRun = async (item) => {
    try {
      await axios.post(`${API}/payroll/wo-items/${item.id}/remove-from-run`, {}, auth(token));
      toast.success('Removed from run'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };
  const markPaid = async (item) => {
    const p = pay[item.id] || {};
    if (!p.payment_date || !p.reference) { toast.error('Payment date and reference are required'); return; }
    try {
      await axios.post(`${API}/payroll/wo-items/${item.id}/mark-payment-received`,
        { payment_date: p.payment_date, reference: p.reference }, auth(token));
      toast.success('Customer payment confirmed — item is now eligible'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not confirm payment'); }
  };

  if (loading) return <div className="h-40 animate-pulse rounded-2xl bg-slate-100" data-testid="wo-queue-loading" />;

  return (
    <div data-testid="payroll-wo-queue">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#0E2A47]">Work-Order Pay</h1>
        <p className="text-sm text-slate-500">ROUT work orders handed to Payroll. Add eligible items to a draft payroll run based on the selected payment timing.</p>
      </div>

      <div className="mb-5 flex flex-wrap gap-2 text-sm" data-testid="wo-queue-counts">
        {ORDER.map((s) => (
          <span key={s} className={`rounded-full px-2.5 py-1 font-semibold ${STATUS_META[s].c}`}>{STATUS_META[s].t}: {counts[s] || 0}</span>
        ))}
      </div>

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="wo-queue-empty">
          No work-order pay items yet. When Operations marks a completed work order “Ready for Payroll,” its workers appear here.
        </div>
      )}

      {ORDER.map((groupStatus) => {
        const group = items.filter((i) => i.status === groupStatus);
        if (group.length === 0) return null;
        return (
          <div key={groupStatus} className="mb-6" data-testid={`wo-queue-group-${groupStatus}`}>
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
              {React.createElement(STATUS_META[groupStatus].icon, { size: 15 })} {STATUS_META[groupStatus].t}
            </div>
            <div className="space-y-2">
              {group.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`wo-item-${item.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-[#0E2A47]">{item.worker_name || '—'} <span className="text-sm font-normal text-slate-400">· {item.worker_role || item.worker_type}</span></div>
                      <div className="text-sm text-slate-500">
                        WO {item.tfs_work_order_number} · {item.customer_name} · Acct {item.account_number} · {item.service_location}
                      </div>
                      <div className="mt-0.5 text-sm text-slate-500">
                        Hours {item.actual_hours} · Completed {fmtDate(item.completion_date)}
                        {item.client_po_number ? ` · PO ${item.client_po_number}` : ''}{item.client_reference ? ` · Ref ${item.client_reference}` : ''}
                      </div>
                      {item.payment_received?.received && (
                        <div className="mt-0.5 text-xs text-emerald-600" data-testid={`wo-item-paid-${item.id}`}>Customer paid {fmtDate(item.payment_received.payment_date)} · Ref {item.payment_received.reference} ({item.payment_received.by_name})</div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-[#0E2A47]" data-testid={`wo-item-payout-${item.id}`}>${Number(item.approved_payout || 0).toFixed(2)}</div>
                      {item.payment_timing_label && <div className="text-xs text-slate-500">{item.payment_timing_label}</div>}
                    </div>
                  </div>

                  {item.status === 'eligible' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                      <select className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid={`wo-item-run-select-${item.id}`}
                        value={sel[item.id] || ''} onChange={(e) => setSel((s) => ({ ...s, [item.id]: e.target.value }))}>
                        <option value="">Select draft run…</option>
                        {eligibleRuns(item).map((r) => <option key={r.id} value={r.id}>{runLabel(r)}</option>)}
                      </select>
                      <Button size="sm" className="bg-[#0E2A47] hover:bg-[#123a63]" data-testid={`wo-item-add-${item.id}`} onClick={() => addToRun(item)}>Add to Run</Button>
                      {item.payment_timing === 'specified_future' && eligibleRuns(item).length === 0 && (
                        <span className="text-xs text-amber-600">No matching draft run yet — create the {item.specified_group} {item.specified_year}-{String(item.specified_month).padStart(2, '0')} run first.</span>
                      )}
                    </div>
                  )}

                  {item.status === 'held_awaiting_payment' && (
                    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`wo-item-held-${item.id}`}>
                      {canPay ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-slate-500">Confirm customer payment:</span>
                          <input type="date" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid={`wo-item-pay-date-${item.id}`}
                            value={(pay[item.id] || {}).payment_date || ''} onChange={(e) => setPay((p) => ({ ...p, [item.id]: { ...p[item.id], payment_date: e.target.value } }))} />
                          <input placeholder="Invoice / payment reference" className="rounded border border-slate-300 px-2 py-1 text-sm" data-testid={`wo-item-pay-ref-${item.id}`}
                            value={(pay[item.id] || {}).reference || ''} onChange={(e) => setPay((p) => ({ ...p, [item.id]: { ...p[item.id], reference: e.target.value } }))} />
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid={`wo-item-mark-paid-${item.id}`} onClick={() => markPaid(item)}>Mark Payment Received</Button>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">Held until a Global Administrator or authorized accounting user confirms customer payment.</div>
                      )}
                    </div>
                  )}

                  {item.status === 'added_to_run' && (
                    <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                      <span className="text-xs text-slate-500">{item.added_to_run?.detail || `Added by ${item.added_to_run?.by_name || ''}`}</span>
                      <Button size="sm" variant="outline" data-testid={`wo-item-remove-${item.id}`} onClick={() => removeFromRun(item)}>
                        <RotateCcw size={12} className="mr-1" /> Remove from Run
                      </Button>
                    </div>
                  )}

                  {item.status === 'recorded_no_pay' && (
                    <div className="mt-1 text-xs text-slate-500">Recorded for the record — this worker’s cost is covered by regular compensation (no additional pay item).</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PayrollWorkOrderQueue;
